import { useCallback, useEffect, useRef } from 'react';

import { getAllFieldsTouched } from '../../common';

import type { FormikValues } from 'formik';
import type { FormFieldName, SaveField } from '../../common';
import type {
  SaveTreatmentPlanV3PageFieldParams,
  TreatmentPlanV3PageFieldCompareValue,
} from './types';

function defaultCompareValue<TValues extends FormikValues>(
  name: FormFieldName<TValues>,
  values: TValues,
): TreatmentPlanV3PageFieldCompareValue<TValues> {
  return values[name];
}

export default function useSaveTreatmentPlanV3PageField<TValues extends FormikValues, TPayload>({
  formik,
  initialValues,
  onUpdate,
  validationSchema,
  toPayload,
  getCompareValue = defaultCompareValue,
  getLinkedFieldNames,
}: SaveTreatmentPlanV3PageFieldParams<TValues, TPayload>) {
  const savedFieldValuesRef = useRef<TValues>({ ...initialValues });
  const savingPromiseRef = useRef(Promise.resolve());

  useEffect(() => {
    savedFieldValuesRef.current = { ...initialValues };
  }, [initialValues]);

  const saveField: SaveField<TValues> = useCallback(
    async (name, value) => {
      const values = value === undefined ? formik.values : { ...formik.values, [name]: value };
      const linkedNames = getLinkedFieldNames?.(name) ?? [];
      const names = [name, ...linkedNames];

      try {
        await Promise.all(names.map((fieldName) => validationSchema.validateAt(fieldName, values)));
      } catch {
        await Promise.all(linkedNames.map((fieldName) => formik.setFieldTouched(fieldName, true)));
        return;
      }

      const currentSave = savingPromiseRef.current
        .catch(() => undefined)
        .then(async () => {
          const changedNames = names.filter(
            (fieldName) =>
              getCompareValue(fieldName, values) !==
              getCompareValue(fieldName, savedFieldValuesRef.current),
          );
          const [firstName, ...otherNames] = changedNames;
          if (!firstName) return;

          const didUpdate = await onUpdate(
            otherNames.reduce(
              (payload, fieldName) => ({ ...payload, ...toPayload(fieldName, values) }),
              toPayload(firstName, values),
            ),
          );
          if (!didUpdate) return;

          await formik.setTouched(getAllFieldsTouched(values), true);
          savedFieldValuesRef.current = changedNames.reduce<TValues>(
            (saved, fieldName) => ({ ...saved, [fieldName]: values[fieldName] }),
            savedFieldValuesRef.current,
          );
        });

      savingPromiseRef.current = currentSave;
      await currentSave;
    },
    [formik, getCompareValue, getLinkedFieldNames, onUpdate, toPayload, validationSchema],
  );

  return { saveField };
}
